import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { KanbanService } from '../../core/services/kanban/kanban.service';
import { ultimos30Dias, erroPeriodo } from './kanban-periodo';
import {
  KanbanBoard,
  KanbanDashboardLinha,
  KanbanDashboardReport,
  KanbanFormularioCampo,
  KanbanNegocio
} from '../../core/models/kanban/kanban';

interface KanbanDashboardBar {
  label: string;
  value: number;
  total: number;
  percent: number;
}

interface KanbanDashboardSeller {
  nome: string;
  total: number;
  ganhos: number;
  perdas: number;
  abertos: number;
}

@Component({
  standalone: false,
  selector: 'app-kanban-dashboard',
  templateUrl: './kanban-dashboard.component.html',
  styleUrls: ['./kanban-dashboard.component.css'],
  preserveWhitespaces: false
})
export class KanbanDashboardComponent implements OnInit {
  usaKanban: boolean = false;
  carregando: boolean = false;
  formularioSelecionado: number = null;
  dataInicial = '';
  dataFinal = '';
  periodoAplicado = '';
  erroFiltro = '';

  board: KanbanBoard = { departamentos: [], tags: [], formularios: [], formulario_campos: [], formulario_opcoes: [] };
  report: KanbanDashboardReport = { formularios: [], campos: [], linhas: [] };
  negocios: KanbanNegocio[] = [];

  constructor(
    private kanbanService: KanbanService,
    private router: Router
  ) { }

  ngOnInit() {
    this.usaKanban = localStorage.getItem('usa_kanban') === 'true';

    if (!this.usaKanban) {
      return;
    }

    this.restaurarPeriodo();
  }

  restaurarPeriodo() {
    if (this.carregando) return;
    const periodo = ultimos30Dias();
    this.dataInicial = periodo.inicio;
    this.dataFinal = periodo.fim;
    this.carregar();
  }

  carregar() {
    if (this.carregando) return;
    this.erroFiltro = erroPeriodo(this.dataInicial, this.dataFinal);
    if (this.erroFiltro) return;
    this.carregando = true;
    const inicio = this.dataInicial;
    const fim = this.dataFinal;

    forkJoin({
      board: this.kanbanService.getBoard(inicio, fim),
      report: this.kanbanService.getDashboardReport(this.formularioSelecionado || null, inicio, fim)
    }).subscribe({ next: resultado => {
      this.board = resultado.board || { departamentos: [], tags: [], formularios: [], formulario_campos: [], formulario_opcoes: [] };
      this.report = resultado.report || { formularios: [], campos: [], linhas: [] };
      this.negocios = this.extrairNegocios(this.board);

      if (!this.formularioSelecionado && this.report.formularios.length > 0) {
        this.formularioSelecionado = this.report.formularios[0].id;
      }

      this.carregando = false;
      this.periodoAplicado = `${inicio.split('-').reverse().join('/')} a ${fim.split('-').reverse().join('/')}`;
    }, error: erro => {
      this.carregando = false;
      this.erroFiltro = erro?.error?.mensagem || 'Não foi possível carregar o período. Tente novamente.';
    } });
  }

  voltarKanban() {
    this.router.navigate(['/kanban']);
  }

  get negociosFormulario(): KanbanNegocio[] {
    if (!this.formularioSelecionado) {
      return this.negocios;
    }

    return this.negocios.filter(n => n.formulario_tipo_id === this.formularioSelecionado);
  }

  get totalCards(): number {
    return this.negociosFormulario.length;
  }

  get totalAbertos(): number {
    return this.negociosFormulario.filter(n => this.statusNegocio(n) === 'EM ABERTO' || this.statusNegocio(n) === 'ATENDIMENTO').length;
  }

  get totalGanhos(): number {
    return this.negociosFormulario.filter(n => this.statusNegocio(n) === 'GANHO').length;
  }

  get totalPerdas(): number {
    return this.negociosFormulario.filter(n => this.statusNegocio(n) === 'PERDA').length;
  }

  get totalAtrasados(): number {
    return this.negociosFormulario.filter(n => this.estaAtrasado(n)).length;
  }

  get tempoMedioAberto(): number {
    const lista = this.negociosFormulario.filter(n => this.statusNegocio(n) !== 'GANHO' && this.statusNegocio(n) !== 'PERDA');
    if (lista.length === 0) {
      return 0;
    }

    return Math.round(lista.reduce((total, n) => total + this.diasDesde(n.data_criacao), 0) / lista.length);
  }

  get conversaoGanho(): number {
    const fechados = this.totalGanhos + this.totalPerdas;
    return fechados > 0 ? Math.round((this.totalGanhos / fechados) * 1000) / 10 : 0;
  }

  get funilPorEtapa(): KanbanDashboardBar[] {
    const total = Math.max(this.totalCards, 1);
    const mapa = this.agrupar(this.negociosFormulario, n => n.etapa_nome || 'Sem etapa');
    return Object.keys(mapa).map(label => ({
      label,
      value: mapa[label],
      total,
      percent: Math.round((mapa[label] / total) * 100)
    })).sort((a, b) => b.value - a.value);
  }

  get agingCards(): KanbanDashboardBar[] {
    const grupos = {
      '0 a 3 dias': 0,
      '4 a 7 dias': 0,
      '8 a 15 dias': 0,
      '+15 dias': 0
    };

    this.negociosFormulario.forEach(n => {
      const dias = this.diasDesde(n.data_criacao);
      if (dias <= 3) {
        grupos['0 a 3 dias']++;
      } else if (dias <= 7) {
        grupos['4 a 7 dias']++;
      } else if (dias <= 15) {
        grupos['8 a 15 dias']++;
      } else {
        grupos['+15 dias']++;
      }
    });

    const total = Math.max(this.totalCards, 1);
    return Object.keys(grupos).map(label => ({
      label,
      value: grupos[label],
      total,
      percent: Math.round((grupos[label] / total) * 100)
    }));
  }

  get produtividadeVendedor(): KanbanDashboardSeller[] {
    const mapa: { [key: string]: KanbanDashboardSeller } = {};

    this.negociosFormulario.forEach(n => {
      const nome = n.seller_responsavel_nome || 'Sem vendedor';
      if (!mapa[nome]) {
        mapa[nome] = { nome, total: 0, ganhos: 0, perdas: 0, abertos: 0 };
      }

      mapa[nome].total++;
      const status = this.statusNegocio(n);
      if (status === 'GANHO') {
        mapa[nome].ganhos++;
      } else if (status === 'PERDA') {
        mapa[nome].perdas++;
      } else {
        mapa[nome].abertos++;
      }
    });

    return Object.keys(mapa).map(k => mapa[k]).sort((a, b) => b.total - a.total).slice(0, 10);
  }

  get maxProdutividadeVendedor(): number {
    const valores = this.produtividadeVendedor
      .map(seller => Math.max(seller.total, seller.ganhos, seller.perdas))
      .concat([1]);

    return Math.max.apply(null, valores);
  }

  produtividadeAltura(valor: number): number {
    if (!valor || this.maxProdutividadeVendedor <= 0) {
      return 0;
    }

    return Math.max(8, Math.round((valor / this.maxProdutividadeVendedor) * 100));
  }

  get topProdutos(): KanbanDashboardBar[] {
    const campoProduto = (this.report.campos || []).find(c => (c.tipo || '').toUpperCase() === 'PRODUTO');
    if (!campoProduto) {
      return [];
    }

    const mapa: { [key: string]: number } = {};
    (this.report.linhas || []).forEach(linha => {
      const valor = this.valorCampo(linha, campoProduto);
      if (valor) {
        mapa[valor] = (mapa[valor] || 0) + 1;
      }
    });

    const max = Math.max.apply(null, Object.keys(mapa).map(k => mapa[k]).concat([1]));
    return Object.keys(mapa).map(label => ({
      label,
      value: mapa[label],
      total: max,
      percent: Math.round((mapa[label] / max) * 100)
    })).sort((a, b) => b.value - a.value).slice(0, 10);
  }

  get movimentoDiario(): KanbanDashboardBar[] {
    const mapa = this.agrupar(this.negociosFormulario, n => this.formatarData(n.data_criacao));
    const max = Math.max.apply(null, Object.keys(mapa).map(k => mapa[k]).concat([1]));
    return Object.keys(mapa).sort((a, b) => this.dataOrdenacao(a) - this.dataOrdenacao(b)).map(label => ({
      label,
      value: mapa[label],
      total: max,
      percent: Math.round((mapa[label] / max) * 100)
    }));
  }

  get cardsCriticos(): KanbanNegocio[] {
    return this.negociosFormulario
      .filter(n => this.estaAtrasado(n))
      .sort((a, b) => this.diasDesde(b.data_criacao) - this.diasDesde(a.data_criacao))
      .slice(0, 8);
  }

  get camposRelatorio(): KanbanFormularioCampo[] {
    return (this.report.campos || []).filter(campo => (campo.tipo || '').toUpperCase() !== 'ARQUIVO');
  }

  statusNegocio(negocio: KanbanNegocio): string {
    if (!negocio) {
      return 'ATENDIMENTO';
    }

    if ((negocio.status || '').toUpperCase() === 'GANHO') {
      return 'GANHO';
    }

    if ((negocio.status || '').toUpperCase() === 'PERDA' || (negocio.status || '').toUpperCase() === 'PERDIDO') {
      return 'PERDA';
    }

    const etapa = this.encontrarEtapa(negocio.etapa_id);
    if (etapa && etapa.tipo === 'GANHO') {
      return 'GANHO';
    }

    if (etapa && etapa.tipo === 'PERDA') {
      return 'PERDA';
    }

    return etapa && etapa.tipo === 'ABERTO' ? 'EM ABERTO' : 'ATENDIMENTO';
  }

  classeStatus(status: string): string {
    const s = (status || '').toUpperCase();
    if (s === 'GANHO') {
      return 'status-ganho';
    }
    if (s === 'PERDA') {
      return 'status-perda';
    }
    if (s === 'EM ABERTO') {
      return 'status-aberto';
    }
    return 'status-atendimento';
  }

  estaAtrasado(negocio: KanbanNegocio): boolean {
    if (!negocio || !negocio.data_fechamento_prevista) {
      return false;
    }

    const status = this.statusNegocio(negocio);
    if (status === 'GANHO' || status === 'PERDA') {
      return false;
    }

    const data = this.parseData(negocio.data_fechamento_prevista);
    if (!data) {
      return false;
    }

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    data.setHours(0, 0, 0, 0);
    return data < hoje;
  }

  diasDesde(data: string): number {
    const parsed = this.parseData(data);
    if (!parsed) {
      return 0;
    }

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    parsed.setHours(0, 0, 0, 0);
    return Math.max(0, Math.floor((hoje.getTime() - parsed.getTime()) / 86400000));
  }

  formatarData(data: string): string {
    const parsed = this.parseData(data);
    return parsed ? parsed.toLocaleDateString('pt-BR') : '';
  }

  formatarDataCurta(data: string): string {
    const parsed = this.parseData(data);
    return parsed ? parsed.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : 'Sem data';
  }

  valorCampo(linha: KanbanDashboardLinha, campo: KanbanFormularioCampo): string {
    if (!linha || !campo || !linha.valores) {
      return '';
    }

    const valor = linha.valores.find(v => v.campo_id === campo.id);
    return valor ? (valor.valor || '') : '';
  }

  exportarExcel() {
    const campos = this.camposRelatorio;
    const headers = ['DATA ABERTURA', 'ID', 'STATUS', 'ETAPA'].concat(campos.map(c => c.nome));
    const rows: string[][] = (this.report.linhas || []).map(linha => {
      return [
        this.formatarData(linha.data_abertura),
        linha.id.toString(),
        linha.status,
        linha.etapa
      ].concat(campos.map(campo => this.valorCampo(linha, campo)));
    });

    const csv = [headers].concat(rows)
      .map(row => row.map(col => this.csvValue(col)).join(';'))
      .join('\r\n');

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'kanban-dashboard.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private extrairNegocios(board: KanbanBoard): KanbanNegocio[] {
    const negocios: KanbanNegocio[] = [];
    (board.departamentos || []).forEach(departamento => {
      (departamento.etapas || []).forEach(etapa => {
        (etapa.negocios || []).forEach(negocio => negocios.push(negocio));
      });
    });
    return negocios;
  }

  private encontrarEtapa(etapaId: number): any {
    for (const departamento of (this.board.departamentos || [])) {
      const etapa = (departamento.etapas || []).find(e => e.id === etapaId);
      if (etapa) {
        return etapa;
      }
    }
    return null;
  }

  private agrupar(lista: KanbanNegocio[], chave: (n: KanbanNegocio) => string): { [key: string]: number } {
    const mapa: { [key: string]: number } = {};
    (lista || []).forEach(item => {
      const key = chave(item) || 'Sem info';
      mapa[key] = (mapa[key] || 0) + 1;
    });
    return mapa;
  }

  private parseData(data: string): Date {
    if (!data) {
      return null;
    }

    const parsed = new Date(data);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private dataOrdenacao(label: string): number {
    const partes = (label || '').split('/');
    if (partes.length !== 3) {
      return 0;
    }

    return (parseInt(partes[2], 10) * 10000) + (parseInt(partes[1], 10) * 100) + parseInt(partes[0], 10);
  }

  private csvValue(value: any): string {
    const text = (value == null ? '' : value.toString()).replace(/"/g, '""');
    return `"${text}"`;
  }
}
